import React, { useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  useValidateInviteTokenQuery,
  useRegisterWithInviteMutation,
} from "../../services/api";
import { setCredentials, selectIsAuthenticated } from "../../store/slices/authSlice";
import ErrorMessage from "../../Components/common/ErrorMessage";
import Loading from "../../Components/common/Loading";
import "./InviteRegistration.css";

const InviteRegistration = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const {
    data: inviteData,
    isLoading: isValidating,
    error: validationError,
  } = useValidateInviteTokenQuery(token, { skip: !token });

  const [registerWithInvite, { isLoading: isRegistering, error: registerError }] =
    useRegisterWithInviteMutation();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [validationErrorMsg, setValidationErrorMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrorMsg) setValidationErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrorMsg("");

    if (formData.password !== formData.confirmPassword) {
      setValidationErrorMsg("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setValidationErrorMsg("Password must be at least 8 characters");
      return;
    }

    try {
      const payload = {
        user: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
          phone: formData.phone || undefined,
        },
        inviteToken: token,
      };

      const response = await registerWithInvite(payload).unwrap();

      // Tokens are in HttpOnly cookies (not in response body)
      if (response.user) {
        dispatch(setCredentials(response));
        navigate("/onboarding");
      }
    } catch (err) {
      console.error("Registration failed:", err);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="invite-reg-page">
        <div className="invite-reg-container">
          <div className="invite-reg-card">
            <div className="invite-reg-loading">
              <Loading />
              <p>Validating your invitation...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already authenticated
  if (isAuthenticated) {
    return (
      <div className="invite-reg-page">
        <div className="invite-reg-container">
          <div className="invite-reg-card">
            <div className="invite-reg-header">
              <h1>Already Logged In</h1>
              <p>You need to log out before registering with an invite link.</p>
            </div>
            <div className="invite-reg-footer">
              <p>
                <Link to="/login" className="login-link">
                  Go to Login
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (validationError || !token) {
    return (
      <div className="invite-reg-page">
        <div className="invite-reg-container">
          <div className="invite-reg-card">
            <div className="invite-reg-header">
              <h1>Invalid Invitation</h1>
              <p>
                This invitation link is invalid or has expired. Please ask your
                team admin to send a new invitation.
              </p>
            </div>
            <div className="invite-reg-footer">
              <p>
                Already have an account?{" "}
                <Link to="/login" className="login-link">
                  Sign in
                </Link>
              </p>
              <p>
                Have an invite code?{" "}
                <Link to="/join-company" className="login-link">
                  Join with code
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const invitation = inviteData?.invitation;
  const company = invitation?.company;

  return (
    <div className="invite-reg-page">
      <div className="invite-reg-container">
        <div className="invite-reg-card">
          <div className="invite-reg-header">
            <h1>You're Invited!</h1>
            <p>Complete your registration to join the team</p>
          </div>

          {/* Company Preview */}
          {company && (
            <div className="invite-company-preview">
              <div className="invite-company-icon">
                {company.name?.charAt(0)?.toUpperCase() || "C"}
              </div>
              <div className="invite-company-info">
                <h4>You're joining</h4>
                <p className="invite-company-name">{company.name}</p>
                <div className="invite-details">
                  <span className="invite-role-badge">{invitation.companyRole}</span>
                  {invitation.invitedBy && (
                    <span className="invite-by">
                      Invited by {invitation.invitedBy.firstName}{" "}
                      {invitation.invitedBy.lastName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {registerError && <ErrorMessage error={registerError} />}
          {validationErrorMsg && (
            <div className="validation-error">{validationErrorMsg}</div>
          )}

          <form onSubmit={handleSubmit} className="invite-reg-form">
            {/* Email (read-only from invitation) */}
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={invitation?.email || ""}
                readOnly
                className="input-readonly"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1234567890"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="At least 8 characters"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter your password"
                required
              />
            </div>

            <button
              type="submit"
              className="invite-reg-btn"
              disabled={isRegistering}
            >
              {isRegistering ? "Creating Account..." : "Join Company"}
            </button>
          </form>

          <div className="invite-reg-footer">
            <p>
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteRegistration;
